import { useState, useEffect } from "react";
import FullScreenModal from "@/components/FullScreenModal";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { formatCurrency, getUsdRate } from "@vaulzo/core/constants";
import { normalizeTravelPointsProgramsForForm } from "@vaulzo/core/domain/travelPointsPrograms";

export default function TravelPointsDialog({
  isOpen,
  onClose,
  onSubmit,
  editingAsset = null,
  exchangeRates = {},
  isLoadingRates = false,
  isSubmitting = false,
}) {
  const [programs, setPrograms] = useState([{ program: "", points: "", points_per_value: "" }]);

  useEffect(() => {
    if (isOpen) {
      if (editingAsset?.travel_points_programs?.length > 0) {
        setPrograms(normalizeTravelPointsProgramsForForm(editingAsset.travel_points_programs));
      } else {
        setPrograms([{ program: "", points: "", points_per_value: "" }]);
      }
    }
  }, [editingAsset, isOpen]);

  const usdRate = getUsdRate(exchangeRates);

  const addProgram = () =>
    setPrograms([...programs, { program: "", points: "", points_per_value: "" }]);

  const removeProgram = (index) => {
    if (programs.length > 1) setPrograms(programs.filter((_, i) => i !== index));
  };

  const updateProgram = (index, field, value) => {
    const updated = [...programs];
    updated[index] = { ...updated[index], [field]: value };
    setPrograms(updated);
  };

  const totalUSD = programs.reduce((sum, p) => {
    const points = parseFloat(p.points) || 0;
    const rate = parseFloat(p.points_per_value) || 0;
    return sum + points * rate;
  }, 0);
  const totalINR = totalUSD * usdRate;

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const validPrograms = programs
      .filter((p) => p.program && p.points && p.points_per_value)
      .map((p) => ({
        program: p.program,
        points: parseFloat(p.points),
        points_per_value: parseFloat(p.points_per_value),
      }));

    if (validPrograms.length === 0) return;

    const totalValueUSD = validPrograms.reduce((sum, p) => sum + p.points * p.points_per_value, 0);
    const totalValueINR = totalValueUSD * usdRate;

    const syntheticEvent = {
      preventDefault: () => {},
      target: {
        elements: {
          name: { value: formData.get("name") || "Travel Points" },
          category: { value: "Travel Points" },
          current_value: { value: totalValueINR.toString() },
        },
      },
      assetData: {
        name: formData.get("name") || "Travel Points",
        category: "Travel Points",
        current_value: totalValueINR,
        total_value_usd: totalValueUSD,
        total_value_inr: totalValueINR,
        asset_type: "travel_points",
        travel_points_programs: validPrograms,
        is_foreign: false,
      },
    };

    onSubmit(syntheticEvent);
  };

  const underline =
    "w-full border-0 border-b-[1.5px] border-vaulzo-input-line bg-transparent py-2 text-base text-vaulzo-ink outline-none transition-colors placeholder:text-vaulzo-subtle focus:border-vaulzo-accent";

  return (
    <FullScreenModal open={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit} className="w-full max-w-[560px]">
        <div className="mb-[14px] text-[12px] uppercase tracking-[0.14em] text-vaulzo-fainter">Asset</div>
        <h2 className="m-0 mb-11 text-[34px] font-bold tracking-[-0.03em] text-vaulzo-ink">
          {editingAsset ? "Edit travel points" : "Add travel points"}
        </h2>

        <div className="flex flex-col gap-[30px]">
          <label className="block">
            <span className="mb-[10px] block text-[13px] font-semibold text-vaulzo-muted">Name</span>
            <input
              name="name"
              required
              placeholder="e.g. Travel Points"
              defaultValue={editingAsset?.name || "Travel Points"}
              className={underline}
            />
          </label>

          <div className="block">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[13px] font-semibold text-vaulzo-muted">Programs</span>
              <button
                type="button"
                onClick={addProgram}
                className="flex items-center gap-[6px] rounded-lg bg-vaulzo-accent-tint px-3 py-[7px] text-[13px] font-semibold text-vaulzo-accent transition-colors hover:bg-vaulzo-level2"
              >
                <Plus className="h-[14px] w-[14px]" />
                Add program
              </button>
            </div>

            <div className="flex flex-col gap-6">
              {programs.map((program, index) => {
                const usd =
                  (parseFloat(program.points) || 0) * (parseFloat(program.points_per_value) || 0);
                return (
                  <div key={index} className="flex items-start gap-4">
                    <div className="flex-1 space-y-4">
                      <input
                        placeholder="Program name (e.g. Amex US)"
                        value={program.program}
                        onChange={(e) => updateProgram(index, "program", e.target.value)}
                        required={index === 0}
                        className={underline}
                      />
                      <div className="flex gap-6">
                        <label className="flex-1">
                          <span className="mb-[6px] block text-[12px] text-vaulzo-fainter">Points</span>
                          <input
                            type="number"
                            inputMode="numeric"
                            placeholder="0"
                            value={program.points}
                            onChange={(e) => updateProgram(index, "points", e.target.value)}
                            required={index === 0}
                            className={`num ${underline}`}
                          />
                        </label>
                        <label className="flex-1">
                          <span className="mb-[6px] block text-[12px] text-vaulzo-fainter">
                            Value per point (USD)
                          </span>
                          <input
                            type="number"
                            step="0.0001"
                            inputMode="decimal"
                            placeholder="0.0200"
                            value={program.points_per_value}
                            onChange={(e) => updateProgram(index, "points_per_value", e.target.value)}
                            required={index === 0}
                            className={`num ${underline}`}
                          />
                        </label>
                      </div>
                      {usd > 0 && (
                        <div className="num text-[12px] text-vaulzo-fainter">
                          ${usd.toFixed(2)} · {formatCurrency(usd * usdRate)}
                        </div>
                      )}
                    </div>
                    {programs.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeProgram(index)}
                        aria-label="Remove program"
                        className="mt-1 p-[6px] text-vaulzo-bar transition-colors hover:text-vaulzo-danger"
                      >
                        <Trash2 className="h-[15px] w-[15px]" strokeWidth={1.7} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-6 flex items-baseline justify-between border-t border-vaulzo-hairline pt-4">
              <span className="text-[13px] font-semibold text-vaulzo-muted">Total value</span>
              <div className="text-right">
                <div className="num text-[18px] font-bold text-vaulzo-ink">{formatCurrency(totalINR)}</div>
                <div className="num text-[12px] text-vaulzo-fainter">${totalUSD.toFixed(2)}</div>
              </div>
            </div>
            {isLoadingRates && (
              <div className="mt-2 text-[12px] text-vaulzo-fainter">Loading exchange rates…</div>
            )}
          </div>
        </div>

        <div className="mt-[52px] flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-[11px] bg-vaulzo-row px-[26px] py-[14px] text-[15px] font-semibold text-vaulzo-muted transition-colors hover:bg-vaulzo-soft disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center rounded-[11px] bg-vaulzo-accent px-[30px] py-[14px] text-[15px] font-semibold text-vaulzo-on-fill transition-colors hover:bg-vaulzo-accent-hover disabled:opacity-60"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : editingAsset ? (
              "Save changes"
            ) : (
              "Add travel points"
            )}
          </button>
        </div>
      </form>
    </FullScreenModal>
  );
}
